const DEFAULT_GA4_MEASUREMENT_ID = "G-VBTVW5Q73Y";

// Prefer env configuration, but fall back to the provided GA4 Measurement ID.
// In CRA, env vars must be prefixed with REACT_APP_.
const measurementId =
  process.env.REACT_APP_GA_MEASUREMENT_ID || DEFAULT_GA4_MEASUREMENT_ID;

// Only send analytics in production by default. This avoids noisy dev traffic and
// also prevents React 18 StrictMode dev-only double-invocations from duplicating events.
const isEnabled =
  process.env.NODE_ENV === "production" &&
  String(process.env.REACT_APP_GA_ENABLED || "true").toLowerCase() !== "false";

let lastTrackedPath = null;
let lastTrackedLocation = "";
let ctaTrackingCleanup = null;

const SAFE_QUERY_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
]);

const START_FREE_HOSTS = new Set(["app.speakyourmenu.com"]);
const START_FREE_PATH_PREFIX = "/signup";
const START_FREE_CTA_NAME = "start_free";

function getGtag() {
  if (typeof window === "undefined") return null;
  if (!window.symAnalyticsConsent?.isAllowed()) return null;
  const gtag = window.gtag;
  return typeof gtag === "function" ? gtag : null;
}

function toUrl(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://speakyourmenu.com";
    return new URL(value, base);
  } catch {
    return null;
  }
}

function sanitizeSearch(searchParams) {
  const clean = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (!SAFE_QUERY_PARAMS.has(String(key || "").toLowerCase())) continue;
    if (!isSafeQueryValue(value)) continue;
    clean.append(key, value);
  }
  const query = clean.toString();
  return query ? `?${query}` : "";
}

function safelyDecodeURIComponent(value) {
  try {
    return decodeURIComponent(String(value || ""));
  } catch {
    return String(value || "");
  }
}

function hasSensitiveValue(value) {
  const raw = String(value || "");
  const decoded = safelyDecodeURIComponent(raw);

  if (!decoded) return false;
  if (decoded.includes("@") || /%40/i.test(raw)) return true;

  if (/(?:^|[?&])(email|e-mail|mail|phone|tel|contact|first_name|last_name)=/i.test(decoded)) {
    return true;
  }

  const digitsOnly = decoded.replace(/\D+/g, "");
  if (digitsOnly.length >= 10 && /^[+()\-.\s\d]+$/.test(decoded)) {
    return true;
  }

  return false;
}

function isSafeQueryValue(value) {
  return !hasSensitiveValue(value);
}

function sanitizePathname(pathname) {
  const rawPathname = String(pathname || "");
  if (!rawPathname) return "/";

  return rawPathname
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "redacted")
    .replace(/%40/gi, "redacted")
    .replace(/\+?\d[\d().\-\s]{8,}\d/g, "redacted");
}

function sanitizePath(pathLike) {
  const parsed = toUrl(pathLike);
  if (!parsed) return "";
  return `${sanitizePathname(parsed.pathname)}${sanitizeSearch(parsed.searchParams)}`;
}

function sanitizeLocation(urlLike) {
  const parsed = toUrl(urlLike);
  if (!parsed) return "";
  return `${parsed.origin}${sanitizePathname(parsed.pathname)}${sanitizeSearch(parsed.searchParams)}`;
}

function getCurrentPagePath() {
  if (typeof window === "undefined") return "";
  return sanitizePath(`${window.location.pathname || ""}${window.location.search || ""}`);
}

function getCurrentPageLocation() {
  if (typeof window === "undefined") return "";
  return sanitizeLocation(window.location.href);
}

function normalizeWebVitalValue(metric) {
  if (!metric || typeof metric.value !== "number") return undefined;
  if (metric.name === "CLS") return Math.round(metric.value * 1000);
  return Math.round(metric.value);
}

function sanitizeEventParams(params) {
  if (!params || typeof params !== "object") return {};

  const out = { ...params };

  if (typeof out.page_path === "string") out.page_path = sanitizePath(out.page_path);
  if (typeof out.page_location === "string") out.page_location = sanitizeLocation(out.page_location);
  if (typeof out.page_referrer === "string") out.page_referrer = sanitizeLocation(out.page_referrer);
  if (typeof out.destination_url === "string") out.destination_url = sanitizeLocation(out.destination_url);

  return out;
}

function normalizeIdentifier(value, fallback) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 64);

  return normalized || fallback;
}

function normalizeCtaName(anchor, destination) {
  const explicit = normalizeIdentifier(anchor.dataset.ctaName, "");
  if (explicit) return explicit;

  if (isStartFreeDestination(destination)) {
    return START_FREE_CTA_NAME;
  }

  const text = String(anchor.textContent || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 64);

  return text || START_FREE_CTA_NAME;
}

function normalizeCtaLocation(anchor) {
  const explicit = normalizeIdentifier(anchor.dataset.ctaLocation, "");
  if (explicit) return explicit;

  const located = anchor.closest("[data-cta-location], section[id], main[id]");
  if (located?.dataset?.ctaLocation) return located.dataset.ctaLocation;
  if (located?.id) return located.id;

  if (typeof window === "undefined") return "unknown";
  const normalizedPath = String(window.location.pathname || "")
    .replace(/^\/+|\/+$/g, "")
    .replace(/\//g, "_");
  return normalizedPath || "home";
}

function isStartFreeDestination(destination) {
  if (!destination) return false;
  if (!START_FREE_HOSTS.has(destination.host)) return false;
  return destination.pathname.startsWith(START_FREE_PATH_PREFIX);
}

export function installMarketingCtaTracking() {
  if (!isEnabled) return () => {};
  if (typeof document === "undefined") return () => {};
  if (ctaTrackingCleanup) return ctaTrackingCleanup;

  const handleClick = (event) => {
    if (!event || event.defaultPrevented) return;
    const targetElement = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!(targetElement instanceof Element)) return;

    const anchor = targetElement.closest("a[href]");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    const destination = toUrl(href || "");
    if (!isStartFreeDestination(destination)) return;

    trackEvent("cta_click", {
      cta_name: normalizeCtaName(anchor, destination),
      cta_location: normalizeCtaLocation(anchor),
      destination_host: destination.host,
      page_path: getCurrentPagePath(),
    });
  };

  document.addEventListener("click", handleClick, true);

  ctaTrackingCleanup = () => {
    document.removeEventListener("click", handleClick, true);
    ctaTrackingCleanup = null;
  };

  return ctaTrackingCleanup;
}

export function trackPageView(path, options = {}) {
  if (!isEnabled) return;
  const gtag = getGtag();
  if (!gtag) return false;
  if (!path) return;

  const normalizedPath = sanitizePath(path);
  if (!normalizedPath) return false;

  const pageLocation = sanitizeLocation(options.pageLocation || getCurrentPageLocation());
  const pageReferrer = sanitizeLocation(
    options.pageReferrer || lastTrackedLocation || (typeof document !== "undefined" ? document.referrer : "")
  );

  if (normalizedPath === lastTrackedPath && pageLocation && pageLocation === lastTrackedLocation) return false;

  lastTrackedPath = normalizedPath;
  if (pageLocation) {
    lastTrackedLocation = pageLocation;
  }

  // SPA-safe page_view. `send_page_view:false` in the global config prevents duplicates.
  const payload = {
    page_path: normalizedPath,
    page_title: options.title || (typeof document !== "undefined" ? document.title : ""),
    page_location: pageLocation,
    send_to: measurementId,
  };

  if (pageReferrer) {
    payload.page_referrer = pageReferrer;
  }

  gtag("event", "page_view", payload);
  return true;
}

export function trackEvent(name, params = {}) {
  if (!isEnabled) return;
  const gtag = getGtag();
  if (!gtag) return;
  if (!name) return;

  gtag("event", name, {
    page_location: getCurrentPageLocation(),
    page_referrer: sanitizeLocation(document.referrer || ""),
    page_title: document.title,
    ...sanitizeEventParams(params),
    send_to: measurementId,
  });
}

export function trackWebVital(metric) {
  if (!metric) return;

  trackEvent("web_vital", {
    metric_name: metric.name,
    metric_id: metric.id,
    metric_value: normalizeWebVitalValue(metric),
    metric_delta: normalizeWebVitalValue({
      name: metric.name,
      value: typeof metric.delta === "number" ? metric.delta : metric.value,
    }),
    metric_rating: metric.rating,
    navigation_type: metric.navigationType,
    page_path: getCurrentPagePath(),
    page_title: typeof document !== "undefined" ? document.title : "",
  });
}
